/* 调律诗篇十周年更新蓝图 — 中国版内容数据。
   Ported verbatim from the Claude Design prototype (roadmap-data.jsx).
   Every translatable field is an inline { locale: string } map.
   Locales: zh-TW (繁中) · en · ja · zh-CN (简中) · ko · es (Español)

   NOTE on i18n: the portal convention is t("key") from useI18n(), but this
   roadmap is a large, deeply-nested content document (cards → bullets/tracks/
   groups). Exploding it into flat locale JSON would be unmaintainable, so the
   content lives here as inline locale maps and is rendered through tx() driven
   by the same useI18n() locale. The 6 design locales match the portal's
   LOCALES exactly (en, ja, zh-TW, zh-CN, ko, es). */

export type Locale = "zh-TW" | "zh-CN" | "ja" | "ko" | "en" | "es";

export type LocaleMap = Partial<Record<Locale, string>>;

/* ─── Language switcher options (short labels, design order) ──────────── */
export const LANGS: { code: Locale; label: string; name: string }[] = [
  { code: "zh-TW", label: "繁中", name: "繁體中文" },
  { code: "en", label: "EN", name: "English" },
  { code: "ja", label: "日本語", name: "日本語" },
  { code: "zh-CN", label: "简中", name: "简体中文" },
  { code: "ko", label: "한국어", name: "한국어" },
  { code: "es", label: "ES", name: "Español" },
];

/* fall back <locale> → en → zh-TW so nothing ever renders blank */
export function tx(map: LocaleMap | undefined, lang: Locale): string {
  if (!map) return "";
  return map[lang] != null
    ? (map[lang] as string)
    : map.en != null
      ? map.en
      : (map["zh-TW"] ?? "");
}

/* ─── Category buckets — gold = content, cool-blue = system, neutral = service ── */
export type BucketKey = "content" | "system" | "service";

export interface Bucket {
  label: LocaleMap;
  color: string;
  tint: string;
  line: string;
}

export const BUCKETS: Record<BucketKey, Bucket> = {
  content: {
    label: {
      "zh-TW": "內容",
      en: "Content",
      ja: "コンテンツ",
      "zh-CN": "内容",
      ko: "콘텐츠",
      es: "Contenido",
    },
    color: "var(--color-accent)",
    tint: "rgba(201,166,107,0.14)",
    line: "rgba(201,166,107,0.55)",
  },
  system: {
    label: {
      "zh-TW": "系統",
      en: "System",
      ja: "システム",
      "zh-CN": "系统",
      ko: "시스템",
      es: "Sistema",
    },
    color: "#9aa6c4",
    tint: "rgba(154,166,196,0.14)",
    line: "rgba(154,166,196,0.5)",
  },
  service: {
    label: {
      "zh-TW": "營運",
      en: "Service",
      ja: "運営",
      "zh-CN": "运营",
      ko: "운영",
      es: "Servicio",
    },
    color: "#dd87a4",
    tint: "rgba(221,135,164,0.15)",
    line: "rgba(221,135,164,0.55)",
  },
};

/* ─── Page chrome strings ────────────────────────────────────────────── */
export const UI = {
  anniversary: {
    "zh-TW": "十週年",
    en: "10th Anniversary",
    ja: "10周年",
    "zh-CN": "十周年",
    ko: "10주년",
    es: "10º Aniversario",
  },
  heroTitle: {
    "zh-TW": "调律诗篇 十週年更新藍圖",
    en: "调律诗篇 Anniversary Update Roadmap",
    ja: "调律诗篇 10周年 アップデート ロードマップ",
    "zh-CN": "调律诗篇 十周年更新蓝图",
    ko: "调律诗篇 10주년 업데이트 로드맵",
    es: "Hoja de ruta del 10º aniversario de 调律诗篇",
  },
  heroSub: {
    "zh-TW": "與你共譜下一章旋律：全新系統與內容更新一覽",
    en: "Let's compose the next chapter together — a look at all-new systems and content updates",
    ja: "次の章の旋律を共に — 新システムとコンテンツ更新の一覧",
    "zh-CN": "与你共谱下一章旋律：全新系统与内容更新一览",
    ko: "다음 장의 선율을 함께 — 새로운 시스템과 콘텐츠 업데이트 한눈에",
    es: "Compongamos juntos el próximo capítulo: un vistazo a los nuevos sistemas y contenidos",
  },
  legend: {
    "zh-TW": "圖例",
    en: "Legend",
    ja: "凡例",
    "zh-CN": "图例",
    ko: "범례",
    es: "Leyenda",
  },
  keyArt: {
    "zh-TW": "曲繪 · 立繪",
    en: "Key Art",
    ja: "キービジュアル",
    "zh-CN": "曲绘 · 立绘",
    ko: "키 아트",
    es: "Key Art",
  },
  screenshot: {
    "zh-TW": "遊戲內截圖",
    en: "In-Game Screenshot",
    ja: "ゲーム画面",
    "zh-CN": "游戏内截图",
    ko: "인게임 스크린샷",
    es: "Captura del juego",
  },
  tba: {
    "zh-TW": "日期未定",
    en: "Date TBD",
    ja: "日程未定",
    "zh-CN": "日期未定",
    ko: "일정 미정",
    es: "Fecha por definir",
  },
  period: {
    "zh-TW": "活動期間",
    en: "Event Period",
    ja: "開催期間",
    "zh-CN": "活动期间",
    ko: "이벤트 기간",
    es: "Periodo del evento",
  },
  footNote: {
    "zh-TW": "更新時程與內容以官方公告為準",
    en: "Schedule and content subject to official announcements",
    ja: "更新時期・内容は公式告知をもって最終とします",
    "zh-CN": "更新时程与内容以官方公告为准",
    ko: "업데이트 일정·내용은 공식 공지를 따릅니다",
    es: "Fechas y contenidos sujetos a anuncios oficiales",
  },
} satisfies Record<string, LocaleMap>;

/* ─── Card / month types ─────────────────────────────────────────────── */
export interface Track {
  name: string;
  artist: string;
}

export interface TrackGroup {
  heading: LocaleMap;
  tracks: Track[];
}

/* A bullet is either a flat locale map, or a parent with optional sub-bullets. */
export type Bullet = LocaleMap | { t: LocaleMap; sub?: LocaleMap[] };

export interface Coda {
  lead: LocaleMap;
  text: LocaleMap;
}

export interface Card {
  id: string;
  bucket: BucketKey;
  tag: LocaleMap;
  title: LocaleMap;
  /* presentation */
  img?: string;
  /* Per-locale override of `img` for screenshots with baked-in UI text. The
     renderer uses imgL10n[locale] when present and otherwise falls back to the
     base `img`, so only the locales that actually differ need an entry. */
  imgL10n?: LocaleMap;
  imgPos?: string;
  /* mobile-only object-position override (600px breakpoint); desktop keeps imgPos */
  imgPosMobile?: string;
  /* mobile-only banner aspect-ratio override (600px breakpoint); when set the
     banner height is driven by the ratio (equivalent to zoom-out) so the image's
     full height is visible instead of a fixed-height crop. Desktop keeps its
     fixed banner height. */
  imgAspectMobile?: string;
  art?: "banner" | "thumb" | "square";
  featured?: boolean;
  keyart?: boolean;
  shot?: boolean;
  smallDecor?: boolean;
  smallTitle?: boolean;
  noMark?: boolean;
  chibi?: string;
  date?: string;
  tba?: boolean;
  period?: string;
  /* body */
  desc?: LocaleMap;
  bulletsLead?: LocaleMap;
  bullets?: Bullet[];
  tracks?: Track[];
  tracksCta?: LocaleMap;
  trackGroups?: TrackGroup[];
  note?: LocaleMap;
  coda?: Coda;
}

export interface Month {
  id: string;
  marker: LocaleMap;
  year: string;
  title: LocaleMap;
  soon?: boolean;
  cards: Card[];
}

/* ─── Roadmap: time windows · cards ──────────────────────────────────── */
export const ROADMAP: Month[] = [
  /* ════════ 1 · July — Core systems ════════ */
  {
    id: "jul-merged",
    marker: {
      "zh-TW": "七月",
      en: "July",
      ja: "7月",
      "zh-CN": "七月",
      ko: "7월",
      es: "Julio",
    },
    year: "2026.07",
    title: {
      "zh-TW": "4.0 登場，全面進化",
      en: "4.0 Is Here",
      ja: "4.0、始動",
      "zh-CN": "4.0 登场，全面进化",
      ko: "4.0, 지금 시작",
      es: "4.0 ya está aquí",
    },
    cards: [
      {
        id: "new-systems",
        img: "art/cards/new-systems-shot.webp",
        imgPosMobile: "right center",
        imgAspectMobile: "1152 / 437",
        imgL10n: {
          en: "art/cards/new-systems-shot-en.webp",
          "zh-CN": "art/cards/new-systems-shot-zh-CN.webp",
          ja: "art/cards/new-systems-shot-ja.webp",
          ko: "art/cards/new-systems-shot-ko.webp",
          es: "art/cards/new-systems-shot-es.webp",
        },
        bucket: "system",
        art: "banner",
        featured: true,
        keyart: true,
        shot: true,
        tag: {
          "zh-TW": "版本更新",
          en: "Version Update",
          ja: "バージョン更新",
          "zh-CN": "版本更新",
          ko: "버전 업데이트",
          es: "Actualización de versión",
        },
        title: {
          "zh-TW": "调律诗篇 4.0 版更新盛大推出！",
          en: "调律诗篇 4.0 Update Arrives!",
          ja: "调律诗篇 4.0 アップデート、堂々登場！",
          "zh-CN": "调律诗篇 4.0 版更新盛大推出！",
          ko: "调律诗篇 4.0 업데이트 대규모 출시!",
          es: "¡Llega la gran actualización 调律诗篇 4.0!",
        },
        desc: {
          "zh-TW":
            "準備好迎接全新的音樂旅程了嗎？本次大改版將帶來更極致的遊玩體驗：",
          en: "Ready for a brand-new musical journey? This major update brings an even more refined play experience:",
          ja: "新たな音楽の旅へ、準備はいい？今回の大型アップデートで、より極上のプレイ体験をお届けします：",
          "zh-CN":
            "准备好迎接全新的音乐旅程了吗？本次大改版将带来更极致的游玩体验：",
          ko: "새로운 음악 여정을 맞이할 준비되셨나요? 이번 대규모 업데이트로 더욱 극한의 플레이 경험을 선사합니다:",
          es: "¿Listo para un viaje musical totalmente nuevo? Esta gran actualización trae una experiencia de juego aún más sublime:",
        },
        bullets: [
          {
            t: {
              "zh-TW":
                "打造專屬主頁：全新主頁畫面，常用功能一鍵直達，還能自由搭配你的專屬風格！",
              en: "Make the home screen yours: a brand-new home with one-tap access to everyday features — styled your way!",
              ja: "自分だけのホームを：全く新しいホーム画面で、よく使う機能にワンタップ。あなたらしく自由に！",
              "zh-CN":
                "打造专属主页：全新主页画面，常用功能一键直达，还能自由搭配你的专属风格！",
              ko: "나만의 홈 꾸미기：완전히 새로운 홈 화면에서 자주 쓰는 기능을 원터치로, 나만의 스타일로 자유롭게!",
              es: "Haz tuya la pantalla de inicio: una pantalla nueva con acceso de un toque a tus funciones habituales, ¡y a tu estilo!",
            },
          },
          {
            t: {
              "zh-TW":
                "挑戰「試煉之地」：突破實力極限，贏取專屬獎牌，向世界展示你的閃耀面板！",
              en: "Challenge the 'Proving Grounds': push past your limits, earn exclusive medals, and show the world your shining stats!",
              ja: "「試練の地」に挑戦：実力の限界を超え、専用メダルを獲得し、輝くパネルを世界へ！",
              "zh-CN":
                "挑战“试炼之地”：突破实力极限，赢取专属奖牌，向世界展示你的闪耀面板！",
              ko: "'시련의 때' 도전：실력의 한계를 돌파하고 전용 메달을 획득해, 빛나는 패널을 세계에 보여주세요!",
              es: "Desafía el «Campo de Pruebas»: supera tus límites, gana medallas exclusivas y muestra al mundo tu panel brillante.",
            },
          },
        ],
      },
      {
        id: "rating-chart",
        chibi: "art/sd/sd-4.png",
        bucket: "system",
        art: "square",
        tag: {
          "zh-TW": "遊玩系統",
          en: "Gameplay",
          ja: "プレイシステム",
          "zh-CN": "游玩系统",
          ko: "플레이 시스템",
          es: "Sistema de juego",
        },
        title: {
          "zh-TW": "分數與 Rating 計算方式更新",
          en: "Score & Rating Calculation Update",
          ja: "スコアとRatingの計算方式を更新",
          "zh-CN": "分数与 Rating 计算方式更新",
          ko: "점수·Rating 계산 방식 갱신",
          es: "Actualización del cálculo de puntuación y Rating",
        },
        desc: {
          "zh-TW":
            "全新升級的計分機制，幫助你更清晰地掌握自己的實力，讓每一次突破自我的成就感更加踏實！",
          en: "A fully upgraded scoring system helps you read your own skill more clearly, making every personal breakthrough feel truly earned!",
          ja: "全面アップグレードされたスコア計算で、自分の実力をより明確に把握。自己ベスト更新の達成感がより確かなものに！",
          "zh-CN":
            "全新升级的计分机制，帮助你更清晰地掌握自己的实力，让每一次突破自我的成就感更加踏实！",
          ko: "전면 업그레이드된 점수 계산 방식으로 자신의 실력을 더 명확하게 파악하고, 매번 자기 기록 경신의 성취감을 더욱 확실하게!",
          es: "Un sistema de puntuación totalmente renovado te ayuda a entender mejor tu nivel, ¡haciendo que cada superación personal se sienta realmente merecida!",
        },
        bullets: [
          {
            "zh-TW":
              "計分機制：將不再計算 Combo 分數，讓成績更加反映演奏的準確度。更新後不會直接刪除既有紀錄，新的計分方式將於更新後首次遊玩開始適用。",
            en: "Scoring: Combo score will no longer be counted, so results better reflect playing accuracy. Existing records won't be deleted outright — the new scoring takes effect from your first play after the update.",
            ja: "スコア計算：Comboスコアを廃止し、成績が演奏の正確さをより反映するように。アップデート後も既存の記録は直接削除されず、新しい計算方式はアップデート後の初プレイから適用されます。",
            "zh-CN":
              "计分机制：将不再计算 Combo 分数，让成绩更加反映演奏的准确度。更新后不会直接删除既有纪录，新的计分方式将于更新后首次游玩开始适用。",
            ko: "점수 계산: Combo 점수를 더 이상 집계하지 않아 성적이 연주의 정확도를 더 잘 반영합니다. 업데이트 후 기존 기록은 즉시 삭제되지 않으며, 새 계산 방식은 업데이트 후 첫 플레이부터 적용됩니다.",
            es: "Puntuación: el puntaje de Combo dejará de contar, para que los resultados reflejen mejor la precisión. Los registros existentes no se borran de inmediato; el nuevo cálculo se aplica desde tu primera partida tras la actualización.",
          },
          {
            "zh-TW":
              "Rating 計算：將重新調整樂曲等級係數與計算公式，使突破極限的挑戰與精準的演奏能獲得更適切的評價。",
            en: "Rating: song-level coefficients and the calculation formula will be retuned, so pushing your limits and playing accurately earn a more fitting evaluation.",
            ja: "Rating計算：楽曲レベル係数と計算式を再調整し、限界への挑戦と精密な演奏がより適切に評価されるようになります。",
            "zh-CN":
              "Rating 计算：将重新调整乐曲等级系数与计算公式，使突破极限的挑战与精准的演奏能获得更适切的评价。",
            ko: "Rating 계산: 악곡 레벨 계수와 계산식을 재조정하여 한계에 대한 도전과 정밀한 연주가 더 적절하게 평가됩니다.",
            es: "Rating: se reajustarán los coeficientes de nivel de las canciones y la fórmula de cálculo, para que superar tus límites y tocar con precisión reciban una valoración más adecuada.",
          },
        ],
        coda: {
          lead: {
            "zh-TW": "遊玩系統新功能",
            en: "New Play-System Feature",
            ja: "プレイシステム新機能",
            "zh-CN": "游玩系统新功能",
            ko: "플레이 시스템 신기능",
            es: "Nueva función del sistema de juego",
          },
          text: {
            "zh-TW":
              "遊玩系統新增「即時分數比較」功能，隨時掌握與自己最高分或好友紀錄的差距，挑戰極限零死角！",
            en: "The play system adds a 'Live Score Comparison' feature — always see the gap to your own best or a friend's record, and chase the limit with zero blind spots!",
            ja: "プレイシステムに「リアルタイムスコア比較」を追加。自己ベストやフレンド記録との差をいつでも把握、限界への挑戦に死角なし！",
            "zh-CN":
              "游玩系统新增“实时分数比较”功能，随时掌握与自己最高分或好友纪录的差距，挑战极限零死角！",
            ko: "플레이 시스템에 '실시간 점수 비교' 기능이 추가되어 자신의 최고 점수나 친구 기록과의 차이를 언제든 확인하며 한계에 도전!",
            es: "El sistema de juego añade la función «Comparación de puntuación en vivo»; consulta en todo momento la diferencia con tu mejor marca o la de un amigo, sin puntos ciegos.",
          },
        },
      },
      {
        id: "sub-update",
        chibi: "art/sd/sd-5.png",
        bucket: "service",
        art: "square",
        noMark: true,
        tag: {
          "zh-TW": "訂閱",
          en: "Subscription",
          ja: "サブスク",
          "zh-CN": "订阅",
          ko: "구독",
          es: "Suscripción",
        },
        title: {
          "zh-TW": "訂閱系統全面升級與營運調整",
          en: "Subscription Overhaul & Service Changes",
          ja: "サブスク全面アップグレードと運営調整",
          "zh-CN": "订阅系统全面升级与运营调整",
          ko: "구독 시스템 전면 업그레이드 및 운영 조정",
          es: "Renovación de suscripciones y ajustes de servicio",
        },
        desc: {
          "zh-TW": "訂閱系統迎來全面升級，轉向以「進階功能」為核心的全新體驗！",
          en: "The subscription system gets a full upgrade, pivoting to a brand-new experience centered on premium features!",
          ja: "サブスクリプションが全面アップグレード。「上位機能」を軸にした全く新しい体験へ！",
          "zh-CN": "订阅系统迎来全面升级，转向以“进阶功能”为核心的全新体验！",
          ko: "구독 시스템이 전면 업그레이드되어 '고급 기능'을 핵심으로 한 완전히 새로운 경험으로 전환됩니다!",
          es: "¡El sistema de suscripción recibe una mejora completa y se reorienta hacia una experiencia centrada en las funciones premium!",
        },
        bullets: [
          {
            t: {
              "zh-TW":
                "訂閱「白金方案」後可完整開啟 4.0 遊玩系統兩項全新功能！",
              en: "Subscribing to the Platinum Plan fully unlocks two all-new 4.0 play-system features!",
              ja: "「プラチナプラン」を購読すると 4.0 プレイシステムの2つの新機能をフル解放！",
              "zh-CN": "订阅“白金方案”后可完整开启 4.0 游玩系统两项全新功能！",
              ko: "'플래티넘 플랜'을 구독하면 4.0 플레이 시스템의 두 가지 새 기능을 완전히 개방합니다!",
              es: "¡Suscribirte al Plan Platino desbloquea por completo dos nuevas funciones del sistema de juego 4.0!",
            },
            sub: [
              {
                "zh-TW":
                  "「遊玩過程重播」功能，保存你的神級操作，再次回味每一個精彩瞬間。",
                en: "'Run Replay' — save your god-tier plays and relive every brilliant moment.",
                ja: "「プレイ過程リプレイ」機能。神プレイを保存して、最高の瞬間を何度でも。",
                "zh-CN":
                  "“游玩过程重播”功能，保存你的神级操作，再次回味每一个精彩瞬间。",
                ko: "플레이 리플레이 기능, 신급 플레이를 저장해 멋진 순간을 다시 감상하세요.",
                es: "Repetición de partidas: guarda tus jugadas maestras y revive cada momento brillante.",
              },
              {
                "zh-TW":
                  "「精準判定顯示」新增 Fast / Slow 時間差的毫秒等級數值顯示，助你完美校準節奏！",
                en: "'Precise Judgment Display' — new millisecond-level Fast / Slow timing-offset values help you perfectly calibrate your rhythm!",
                ja: "「精密判定表示」：Fast / Slow のミリ秒単位のタイミング差数値を新たに追加、リズムを完璧に補正！",
                "zh-CN":
                  "“精准判定显示”新增 Fast / Slow 时间差的毫秒级数值显示，助你完美校准节奏！",
                ko: "'정밀 판정 표시' Fast / Slow 시간차의 밀리초 단위 수치를 새로 추가해 리듬을 완벽하게 보정!",
                es: "«Indicador de juicio preciso»: nuevos valores de desfase Fast / Slow a nivel de milisegundo para calibrar tu ritmo a la perfección.",
              },
            ],
          },
          {
            "zh-TW":
              "訂閱服務即將支援 调律诗篇 帳號綁定，連結 调律诗篇 帳號後可使用「调律诗篇 玩家入口網頁」進階功能。",
            en: 'The subscription service will soon support 调律诗篇 account linking; once linked, you can use the advanced features of the "调律诗篇 Player Portal" web page.',
            ja: "サブスクリプションが 调律诗篇 アカウント連携に対応予定。调律诗篇 アカウントを連携すると「调律诗篇 プレイヤーポータル」の上位機能が利用できます。",
            "zh-CN":
              "订阅服务即将支持 调律诗篇 账号绑定，连结 调律诗篇 账号后可使用“调律诗篇 玩家入口网页”进阶功能。",
            ko: "구독 서비스가 곧 调律诗篇 계정 연동을 지원합니다. 调律诗篇 계정을 연동하면 '调律诗篇 플레이어 포털' 웹페이지의 고급 기능을 사용할 수 있습니다.",
            es: "El servicio de suscripción pronto admitirá la vinculación de la cuenta de 调律诗篇; tras vincularla, podrás usar las funciones avanzadas de la página «Portal del Jugador de 调律诗篇».",
          },
        ],
      },
      {
        id: "portal",
        chibi: "art/sd/sd-6.png",
        bucket: "system",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "全新功能",
          en: "New Feature",
          ja: "新機能",
          "zh-CN": "全新功能",
          ko: "신규 기능",
          es: "Nueva función",
        },
        title: {
          "zh-TW": "调律诗篇 玩家入口網頁全新上線！",
          en: "调律诗篇 Portal Is Now Live!",
          ja: "调律诗篇 プレイヤーポータル、新登場！",
          "zh-CN": "调律诗篇 玩家入口网页全新上线！",
          ko: "调律诗篇 플레이어 포털 새롭게 오픈!",
          es: "¡Portal de Jugador 调律诗篇 ya disponible!",
        },
        desc: {
          "zh-TW": "在全新的玩家入口網站中，追蹤自己的遊玩歷程，一覽進步成果！",
          en: "Track your play history and see how far you've come — all on the brand-new player portal!",
          ja: "新しいプレイヤーポータルで、自分のプレイ履歴を追跡し、成長の成果を一望しよう！",
          "zh-CN": "在全新的玩家入口网站中，追踪自己的游玩历程，一览进步成果！",
          ko: "새로운 플레이어 포털에서 자신의 플레이 기록을 추적하고, 성장의 결과를 한눈에 확인하세요!",
          es: "Sigue tu historial de juego y comprueba cuánto has progresado, ¡todo en el nuevo portal del jugador!",
        },
        bulletsLead: {
          "zh-TW": "主打功能：",
          en: "Key features:",
          ja: "主な機能：",
          "zh-CN": "主打功能：",
          ko: "주요 기능:",
          es: "Funciones principales:",
        },
        bullets: [
          {
            "zh-TW": "個人檔案：展示過關統計、近期遊玩與成長紀錄。",
            en: "Profile: clear stats, recent plays, and your growth record.",
            ja: "プロフィール：クリア統計・最近のプレイ・成長記録を表示。",
            "zh-CN": "个人档案：展示过关统计、近期游玩与成长纪录。",
            ko: "프로필: 클리어 통계, 최근 플레이, 성장 기록을 표시.",
            es: "Perfil: estadísticas de clears, partidas recientes y tu progreso.",
          },
          {
            "zh-TW": "成績分析：查看樂曲成績與 Rating 構成。",
            en: "Score analysis: view song scores and your Rating breakdown.",
            ja: "スコア分析：楽曲スコアとRatingの構成を確認。",
            "zh-CN": "成绩分析：查看乐曲成绩与 Rating 构成。",
            ko: "스코어 분석: 악곡 점수와 Rating 구성을 확인.",
            es: "Análisis de puntuación: consulta los scores y la composición de tu Rating.",
          },
          {
            "zh-TW": "好友比較：與好友互相比較成績，追蹤彼此的進步。",
            en: "Friend comparison: compare scores with friends and track each other's progress.",
            ja: "フレンド比較：フレンドとスコアを比較し、互いの成長を追跡。",
            "zh-CN": "好友比较：与好友互相比较成绩，追踪彼此的进步。",
            ko: "친구 비교: 친구와 점수를 비교하고 서로의 성장을 추적.",
            es: "Comparación con amigos: compara puntuaciones y sigue el progreso mutuo.",
          },
        ],
        note: {
          "zh-TW": "*使用 调律诗篇 帳號登入。部分功能需訂閱以開啟。",
          en: "*Sign in with your 调律诗篇 account. Some features require a subscription.",
          ja: "*调律诗篇アカウントでログイン。一部機能の利用にはサブスクが必要です。",
          "zh-CN": "*使用 调律诗篇 账号登录。部分功能需订阅以开启。",
          ko: "*调律诗篇 계정으로 로그인하세요. 일부 기능은 구독이 필요합니다.",
          es: "*Inicia sesión con tu cuenta 调律诗篇. Algunas funciones requieren suscripción.",
        },
      },
      {
        /* 備註：墮落迴響為新活動，尚無正式素材。依評論以文字為主、僅用零散小人(face)裝飾——避免大圖造成過強的內容聯想。原 img: art/cards/event-38.jpg 已移除。 */
        /* 備註：墮落迴響為新活動，依評論小人指定使用 event35_7.png。 */
        id: "event-38",
        img: "art/cards/dreadmourn_texture.webp",
        imgPos: "center 30%",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "限時活動",
          en: "Time-Limited Event",
          ja: "期間限定イベント",
          "zh-CN": "限时活动",
          ko: "기간 한정 이벤트",
          es: "Evento por tiempo limitado",
        },
        title: {
          "zh-TW": "限時活動「墮落迴響」",
          en: 'Time-Limited Event "Fallen Resonance"',
          ja: "期間限定イベント「堕落の反響」",
          "zh-CN": "限时活动：堕落回响",
          ko: '기간 한정 이벤트 "타락의 울림"',
          es: 'Evento de tiempo limitado "Resonancia Caída"',
        },
        desc: {
          "zh-TW":
            "別睜開眼，朝向那片甜蜜的虛無墜落。\n全新限時活動收錄三首黑暗系神曲。\n來吧，一同沉入這場不被允許醒來的甜美夢境。",
          en: "The abyss is calling, and it is a one-way journey.\nThis event features three masterpieces of darkness.\nCome, and lose yourself in a sweet nightmare you aren't allowed to wake from.",
          ja: "甘美な虚無へ、堕ちていくの——\n限定イベントは闇を纏う三つの神曲を収録。\nさあ、二度と醒めぬ夢へ。",
          "zh-CN":
            "别睁开眼，向着那片甜蜜的虚无坠落吧。\n全新限时活动收录三首黑暗系神曲。\n来吧，一同沉入这场不被允许醒来的甜美梦境。",
          ko: "눈을 감으세요. 달콤한 허무를 향해 추락할 시간입니다.\n이번 한정 이벤트는 어둠을 품은 세 곡의 명곡을 수록합니다.\n깨어날 수 없는 꿈속으로 당신을 초대합니다.",
          es: "No abras los ojos. Déjate caer en la dulce nada.\nEste evento por tiempo limitado incluye tres obras maestras de la oscuridad.\n¡Entra y ríndete a esta resonancia del vacío!",
        },
      },
    ],
  },

  /* ════════ 2 · August — Reunion I & II & Virtual Singer ════════ */
  {
    id: "aug",
    marker: {
      "zh-TW": "八月",
      en: "August",
      ja: "8月",
      "zh-CN": "八月",
      ko: "8월",
      es: "Agosto",
    },
    year: "2026.08",
    title: {
      "zh-TW": "星空開唱，重逢續章",
      en: "Sing Under the Stars",
      ja: "星空ライブ、再び",
      "zh-CN": "星空开唱，重逢续章",
      ko: "별빛 라이브, 다시 만나",
      es: "Canta bajo las estrellas",
    },
    cards: [
      {
        id: "reunion-1",
        img: "art/cards/reunion-1.webp",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "樂曲章節",
          en: "Song Chapter",
          ja: "楽曲チャプター",
          "zh-CN": "乐曲章节",
          ko: "악곡 챕터",
          es: "Capítulo musical",
        },
        title: {
          "zh-TW": "重逢之音 Phase I",
          en: "Sound of Reunion: Phase I",
          ja: "再会の音 フェーズ I",
          "zh-CN": "重逢之音 Phase I",
          ko: "재회의 소리 페이즈 I",
          es: "Sonido de Reencuentro: Phase I",
        },
        desc: {
          "zh-TW":
            "十週年紀念章節「重逢之音 Phase I」收錄全新原創樂曲，並同步推出角色收藏！",
          en: 'The 10th Anniversary Chapter "Sound of Reunion: Phase I" features all-new original tracks, with a character collection launching alongside!',
          ja: "10周年記念チャプター「再会の音 フェーズ I」は全曲オリジナル新曲を収録、キャラクターコレクションも同時登場！",
          "zh-CN":
            "十周年纪念章节“重逢之音 Phase I”收录全新原创乐曲，并同步推出角色收藏！",
          ko: "10주년 기념 챕터 '재회의 소리 페이즈 I'은 완전히 새로운 오리지널 악곡을 수록하며, 캐릭터 컬렉션도 동시 출시됩니다!",
          es: "El capítulo del 10º Aniversario «Sonido de Reencuentro: Phase I» incluye temas originales totalmente nuevos, ¡con una colección de personajes que llega al mismo tiempo!",
        },
      },
    ],
  },

  /* ════════ 3 · September — Wafū pack ════════ */
  {
    id: "sep-late",
    marker: {
      "zh-TW": "九月",
      en: "September",
      ja: "9月",
      "zh-CN": "九月",
      ko: "9월",
      es: "Septiembre",
    },
    year: "2026.09",
    title: {
      "zh-TW": "和風來襲，沉浸開玩",
      en: "Wafū Vibes Incoming",
      ja: "和の波、来襲",
      "zh-CN": "和风来袭，沉浸开玩",
      ko: "일본풍 감성 입고",
      es: "Llega el toque oriental",
    },
    cards: [
      {
        id: "reunion-2",
        img: "art/cards/reunion-2.webp",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "樂曲章節",
          en: "Song Chapter",
          ja: "楽曲チャプター",
          "zh-CN": "乐曲章节",
          ko: "악곡 챕터",
          es: "Capítulo musical",
        },
        title: {
          "zh-TW": "重逢之音 Phase II",
          en: "Sound of Reunion: Phase II",
          ja: "再会の音 フェーズ II",
          "zh-CN": "重逢之音 Phase II",
          ko: "재회의 소리 페이즈 II",
          es: "Sonido de Reencuentro: Phase II",
        },
        desc: {
          "zh-TW":
            "十週年紀念章節「重逢之音 Phase II」收錄全新原創樂曲，並同步推出角色收藏！",
          en: 'The 10th Anniversary Chapter "Sound of Reunion: Phase II" features all-new original tracks, with a character collection launching alongside!',
          ja: "10周年記念チャプター「再会の音 フェーズ II」は全曲オリジナル新曲を収録、キャラクターコレクションも同時登場！",
          "zh-CN":
            "十周年纪念章节“重逢之音 Phase II”收录全新原创乐曲，并同步推出角色收藏！",
          ko: "10주년 기념 챕터 '재회의 소리 페이즈 II'은 완전히 새로운 오리지널 악곡을 수록하며, 캐릭터 컬렉션도 동시 출시됩니다!",
          es: "El capítulo del 10º Aniversario «Sonido de Reencuentro: Phase II» incluye temas originales totalmente nuevos, ¡con una colección de personajes que llega al mismo tiempo!",
        },
      },
      {
        /* 備註：虛擬星群為新活動，尚無正式素材，依評論改用小人(face)裝飾。原 img: art/cards/event-39.jpg 已移除。 */
        id: "event-39",
        img: "art/cards/brand-new-day.webp",
        imgPos: "center 30%",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "限時活動",
          en: "Time-Limited Event",
          ja: "期間限定イベント",
          "zh-CN": "限时活动",
          ko: "기간 한정 이벤트",
          es: "Evento por tiempo limitado",
        },
        title: {
          "zh-TW": "限時活動「虛擬學園」",
          en: 'Time-Limited Event "Virtual Campus"',
          ja: "期間限定イベント「仮想学園」",
          "zh-CN": "限时活动：虚拟学园",
          ko: '기간 한정 이벤트 "가상캠퍼스"',
          es: 'Evento de tiempo limitado "Campus Virtual"',
        },
        desc: {
          "zh-TW":
            "衝破走廊，奔向地圖之外的無限未來！\n全新限時活動收錄虛擬歌手所演唱的三首樂曲，其中包含一首原創新曲。\n關於青春、叛逆與覺醒的校園巡禮，由此啟程！",
          en: "Break through the hallways and sprint toward the infinite future!\nThis new time-limited event features three tracks performed by virtual singers, including one brand-new original song.\nA journey of youth, rebellion, and awakening begins here!",
          ja: "廊下を突き抜け、地図にない無限の未来へ！\n新たな期間限定イベントは、バーチャルシンガーが歌う三曲を収録し、そのうち一曲はオリジナル新曲です。\n青春と反逆、そして覚醒を巡る校舎巡礼が、ここから始まります！",
          "zh-CN":
            "冲破走廊，奔向地图之外的无限未来！\n全新限时活动收录虚拟歌手所演唱的三首乐曲，其中包含一首原创新曲。\n关于青春、叛逆与觉醒的校园巡礼，由此启程！",
          ko: "복도를 뚫고, 지도 너머의 무한한 미래를 향해 질주하라!\n새로운 기간 한정 이벤트는 버추얼 싱어가 부르는 세 곡을 수록하며, 그중 한 곡은 오리지널 신곡입니다.\n청춘과 방황, 그리고 성찰의 학원 순례가 지금 시작됩니다!",
          es: "¡Cruza los pasillos y corre hacia el futuro infinito más allá del mapa!\nEl nuevo evento por tiempo limitado incluye tres canciones interpretadas por cantantes virtuales, entre ellas un tema original totalmente nuevo.\n¡Una travesía de juventud, rebelión y despertar comienza aquí!",
        },
      },
    ],
  },

  /* ════════ 4 · Coming soon ════════ */
  {
    id: "coming-soon",
    marker: {
      "zh-TW": "旅程繼續",
      en: "The Journey Continues",
      ja: "旅は続いていく",
      "zh-CN": "旅程继续",
      ko: "여정은 계속됩니다",
      es: "El viaje continúa",
    },
    year: "",
    soon: true,
    title: {
      "zh-TW": "更多故事與旋律，敬請期待",
      en: "More stories and melodies await",
      ja: "物語と旋律は、まだまだこれから！",
      "zh-CN": "更多故事与旋律，敬请期待",
      ko: "더 많은 이야기와 선율이 기다립니다",
      es: "Más historias y melodías te esperan",
    },
    cards: [
      {
        id: "side-story",
        img: "art/cards/r12-expansion-map.webp",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "樂曲章節",
          en: "Song Chapter",
          ja: "楽曲チャプター",
          "zh-CN": "乐曲章节",
          ko: "악곡 챕터",
          es: "Capítulo musical",
        },
        title: {
          "zh-TW": "過往之聲",
          en: "Echoes of the Past",
          ja: "過去の残響",
          "zh-CN": "过往之声",
          ko: "지난날의 목소리",
          es: "Voz del Pasado",
        },
        desc: {
          "zh-TW":
            "收錄 调律诗篇 樂曲公募「過往之聲」入選樂曲的全新支線章節系列即將推出！",
          en: 'The all-new side-story series featuring the winning tracks from the 调律诗篇 original song contest "Echoes of the Past" is coming soon!',
          ja: "调律诗篇 オリジナル楽曲公募「過去の残響」の入選楽曲を収録した、全く新しいサイドストーリーシリーズがまもなく登場！",
          "zh-CN":
            "收录 调律诗篇 乐曲公募“过往之声”入选乐曲的全新支线章节系列即将推出！",
          ko: "调律诗篇 오리지널 곡 공모전 '지난날의 목소리' 입선공을 수록한 완전히 새로운 사이드 스토리 시리즈가 곧 출시됩니다!",
          es: "¡Llega pronto la nueva serie de Side Story que incluye las canciones ganadoras del concurso de canciones original de 调律诗篇 «Voz del Pasado»!",
        },
      },
      {
        id: "wafu-t6",
        img: "art/cards/wafu-night.webp",
        imgPos: "center 50%",
        bucket: "content",
        art: "banner",
        featured: true,
        tag: {
          "zh-TW": "樂曲章節",
          en: "Song Chapter",
          ja: "楽曲チャプター",
          "zh-CN": "乐曲章节",
          ko: "악곡 챕터",
          es: "Capítulo musical",
        },
        title: {
          "zh-TW": "夏日祭典",
          en: "Summer Festival",
          ja: "夏祭り",
          "zh-CN": "夏日祭典",
          ko: "여름 축제",
          es: "Festival de Verano",
        },
        desc: {
          "zh-TW":
            "收錄 调律诗篇 樂曲公募「過往之聲」入選樂曲 5 首，抓住夏天的尾巴，一起在燦爛煙火的夜空下納涼！",
          en: 'Featuring five winning tracks from the 调律诗篇 original song contest "Echoes of the Past" — catch the tail end of summer and cool off together under a sky of dazzling fireworks!',
          ja: "调律诗篇 オリジナル楽曲公募「過去の残響」の入選楽曲 5 曲を収録。夏の終わりをつかまえて、きらめく花火の夜空の下で一緒に涼もう！",
          "zh-CN":
            "收录 调律诗篇 乐曲公募“过往之声”入选乐曲 5 首，抓住夏天的尾巴，一起在灿烂烟火的夜空下纳凉！",
          ko: "调律诗篇 오리지널 곡 공모전 '지난날의 목소리' 입선곡 5곡을 수록. 여름의 끝자락을 붙잡고, 화려한 불꽃이 수놓는 밤하늘 아래에서 함께 더위를 식혀요!",
          es: "Incluye cinco canciones ganadoras del concurso de canciones original de 调律诗篇 «Echoes of the Past»: ¡aprovecha el final del verano y refréscate bajo un cielo de deslumbrantes fuegos artificiales!",
        },
      },
    ],
  },
];
